import { PACE_OPTIONS, paceLabel, type PlayingPace } from '@league/shared/schemas';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useSearchParams } from 'react-router-dom';
import { useEffect, useState } from 'react';

import type { InvitePlayersResponse, SubstitutePoolEntry, SubstitutePoolResponse } from '@league/shared/contract';

import { Facts, MobileList, MobileRow, pacePhrase, Sheet, SHEET_DANGER, SHEET_DONE, subbedPhrase } from '../../components/MobileList';
import { PLAYER_NAME, PLAYER_ROW, PlayerTable, QUIET_ACTION, Td, Th, ThActions } from '../../components/PlayerTable';
import { Button, ErrorNote, Field, Input } from '../../components/ui';
import { ApiError, apiFetch, apiPatch, apiPost } from '../../lib/api';
import { ACCOUNT_LABEL, InviteButton } from './SignInInvite';

/**
 * THE MARKET'S SUBSTITUTE POOL — who can fill an empty seat, on which sessions, and how often they
 * already have.
 *
 * Phase 3's seating board reads this list, which is the whole reason it exists now rather than
 * later: something has to write it before the board can seat anybody from it.
 *
 * Per MARKET, not per league and not per director. Subs are the one resource directors in a city
 * genuinely share, and the second league in Charlotte should inherit Charlotte's subs rather than
 * start from nobody.
 */

interface Market {
  id: string;
  name: string;
  city: string;
  state: string;
}

type SubEntry = SubstitutePoolEntry;

/** 0 = Sunday, matching the column. Weeknights first, because leagues run on them. */
const SESSIONS = [
  { value: 1, label: 'Mon' },
  { value: 2, label: 'Tue' },
  { value: 3, label: 'Wed' },
  { value: 4, label: 'Thu' },
  { value: 5, label: 'Fri' },
  { value: 6, label: 'Sat' },
  { value: 0, label: 'Sun' },
] as const;

function parseSessions(value: string | null): number[] {
  if (!value) return [];
  return value
    .split(',')
    .map((n) => Number(n.trim()))
    .filter((n) => Number.isInteger(n) && n >= 0 && n <= 6);
}

export function SubstitutesPage() {
  const queryClient = useQueryClient();
  const [params] = useSearchParams();
  const [marketId, setMarketId] = useState<string>(params.get('market') ?? '');
  const [adding, setAdding] = useState(false);
  /** The substitute whose panel is open on a phone. */
  const [openId, setOpenId] = useState<string | null>(null);

  const markets = useQuery({
    queryKey: ['markets'],
    queryFn: () => apiFetch<{ markets: Market[] }>('/api/markets'),
  });

  /**
   * Defaults to the market SHE RUNS IN, not the first one alphabetically.
   *
   * Markets are shared reference data so the dropdown lists all of them, and the first version
   * landed a Raleigh director on Charlotte's pool — technically correct, immediately wrong. Her own
   * league's market is the only sensible opening guess; the alphabetical first is the fallback for
   * a director with no leagues yet.
   */
  const myLeagues = useQuery({
    queryKey: ['leagues'],
    queryFn: () => apiFetch<{ leagues: { marketId: string }[] }>('/api/leagues'),
  });

  useEffect(() => {
    if (marketId) return;

    /**
     * BOTH queries have to have settled first. Markets usually resolves first, and picking from it
     * the moment it arrives sets `marketId` — after which this effect returns early forever and her
     * own market never gets a look in. The bug was invisible in code and obvious on screen: a
     * Raleigh director staring at Charlotte's pool.
     */
    if (markets.isPending || myLeagues.isPending) return;

    const next = myLeagues.data?.leagues[0]?.marketId ?? markets.data?.markets[0]?.id;
    if (next) setMarketId(next);
  }, [markets.data, markets.isPending, myLeagues.data, myLeagues.isPending, marketId]);

  const subs = useQuery({
    queryKey: ['substitutes', marketId],
    queryFn: () => apiFetch<SubstitutePoolResponse>(`/api/markets/${marketId}/substitutes`),
    enabled: Boolean(marketId),
  });

  useEffect(() => {
    if (subs.data && window.location.hash.startsWith('#sub-')) document.getElementById(window.location.hash.slice(1))?.focus();
  }, [subs.data]);

  const refresh = () => Promise.all([
    queryClient.invalidateQueries({ queryKey: ['substitutes', marketId] }),
    queryClient.invalidateQueries({ queryKey: ['league-subs'] }),
  ]);

  const update = useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: Record<string, unknown> }) =>
      apiPatch(`/api/substitutes/${id}`, patch),
    onSuccess: refresh,
  });

  /*
    Optional, ruled 2026-09-25: a sub's confirmation, reminders and recorded play never depend on a
    login. This is only for the sub who wants to see her table and fill in its card herself.
  */
  const [inviteNote, setInviteNote] = useState<string | null>(null);
  const invite = useMutation({
    mutationFn: (entry: SubEntry) =>
      apiPost<InvitePlayersResponse>(`/api/substitutes/${entry.substitute.id}/invitation`, {}),
    onSuccess: async ({ invited }, entry) => {
      setInviteNote(
        invited === 1
          ? `Sent ${entry.player.firstName} an invitation to sign in.`
          : `Nothing to send ${entry.player.firstName}.`,
      );
      await refresh();
    },
  });

  const entries = subs.data?.substitutes ?? [];
  const available = entries.filter((e) => e.substitute.active);
  const standDown = entries.filter((e) => !e.substitute.active);

  return (
    <section className="flex flex-col gap-5">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <Field label="Market" htmlFor="sub-market" className="min-w-56">
          <select
            id="sub-market"
            value={marketId}
            onChange={(e) => setMarketId(e.target.value)}
            className="h-11 w-full rounded border border-control-border bg-paper px-3 text-[15px] focus-visible:border-deep"
          >
            {(markets.data?.markets ?? []).map((market) => (
              <option key={market.id} value={market.id}>
                {market.city}, {market.state}
              </option>
            ))}
          </select>
        </Field>

        {/* One button, one job: Cancel lives in the form beside its Add, as everywhere else. */}
        {adding ? null : (
          <Button size="app" onClick={() => setAdding(true)}>
            Add a substitute
          </Button>
        )}
      </div>

      {/*
        Said plainly, because a director who thinks this list is hers will not add anybody to it.
        Sharing is the feature.
      */}
      <p className="text-[13px] text-ink-2">
        This pool belongs to the market, not to one league — every director running here can call on
        it, and add to it.
      </p>

      {adding && marketId ? (
        <AddSubstitute
          marketId={marketId}
          onCancel={() => setAdding(false)}
          onDone={() => {
            setAdding(false);
            void refresh();
          }}
        />
      ) : null}

      {update.error ? (
        <ErrorNote>
          {update.error instanceof ApiError ? update.error.message : 'Could not save that.'}
        </ErrorNote>
      ) : null}

      {inviteNote ? <p role="status" className="text-[13px] text-ink-2">{inviteNote}</p> : null}
      {invite.error ? (
        <ErrorNote>
          {invite.error instanceof ApiError ? invite.error.message : 'Could not send the invitation.'}
        </ErrorNote>
      ) : null}

      {subs.isPending && marketId ? <p className="text-sm text-ink-3">Loading…</p> : null}

      {!subs.isPending && entries.length === 0 ? (
        <p className="rounded-md border border-dashed border-light px-5 py-8 text-center text-[13px] text-ink-3">
          Nobody in this market’s pool yet. The seating board calls on this list when somebody is
          away.
        </p>
      ) : null}

      {available.length > 0 ? (
        <PlayerTable head={<><Th>Player</Th><Th>Pace of play</Th><Th>Sessions</Th><Th>Times subbed</Th><ThActions /></>}>
          {available.map((entry) => (
            <SubRow
              key={entry.substitute.id}
              entry={entry}
              pending={update.isPending}
              inviting={invite.isPending}
              onInvite={() => invite.mutate(entry)}
              onSessions={(sessions) =>
                update.mutate({
                  id: entry.substitute.id,
                  patch: { availableWeekdays: sessions },
                })
              }
              onStandDown={() =>
                update.mutate({ id: entry.substitute.id, patch: { active: false } })
              }
            />
          ))}
        </PlayerTable>
      ) : null}

      {available.length > 0 ? (
        <MobileList>
          {available.map((entry) => {
            const days = parseSessions(entry.substitute.availableWeekdays);
            return (
              <MobileRow
                key={entry.substitute.id}
                firstName={entry.player.firstName}
                lastName={entry.player.lastName}
                onOpen={() => setOpenId(entry.substitute.id)}
                lines={[
                  `${pacePhrase(paceLabel(entry.player.preferredPace), !entry.player.preferredPace || entry.player.preferredPace === 'unknown')} · ${subbedPhrase(entry.timesSubbed)}`,
                  days.length ? `Available ${SESSIONS.filter((d) => days.includes(d.value)).map((d) => d.label).join(', ')}` : <span className="text-ink-3">Days not asked yet</span>,
                ]}
              />
            );
          })}
        </MobileList>
      ) : null}

      {available.filter((entry) => entry.substitute.id === openId).map((entry) => (
        <SubSheet
          key={entry.substitute.id}
          entry={entry}
          pending={update.isPending}
          inviting={invite.isPending}
          onClose={() => setOpenId(null)}
          onInvite={() => invite.mutate(entry)}
          onSessions={(sessions) => update.mutate({ id: entry.substitute.id, patch: { availableWeekdays: sessions } })}
          onStandDown={() => { setOpenId(null); update.mutate({ id: entry.substitute.id, patch: { active: false } }); }}
        />
      ))}

      {standDown.length > 0 ? (
        <div className="flex flex-col gap-2">
          <h2 className="text-[11px] font-semibold uppercase tracking-[0.14em] text-ink-3">
            Removed substitutes
          </h2>
          <ul className="flex flex-col divide-y divide-light rounded-md border border-light">
            {standDown.map((entry) => (
              <li
                key={entry.substitute.id}
                className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1 px-5 py-3"
              >
                <span className="text-ink-2">
                  <Link to={`/director/players/${entry.player.id}`} className="underline underline-offset-4 focus-visible:outline-2 focus-visible:outline-offset-4">{entry.player.firstName} {entry.player.lastName}</Link>
                  {entry.timesSubbed > 0 ? (
                    <span className="tabular text-[13px]"> · subbed {entry.timesSubbed}×</span>
                  ) : null}
                </span>
                <button
                  type="button"
                  className={`${QUIET_ACTION} hover:border-deep`}
                  onClick={() =>
                    update.mutate({ id: entry.substitute.id, patch: { active: true } })
                  }
                >
                  Return to sub list
                </button>
              </li>
            ))}
          </ul>
        </div>
      ) : null}
    </section>
  );
}

function SubRow({
  entry,
  pending,
  inviting,
  onInvite,
  onSessions,
  onStandDown,
}: {
  entry: SubEntry;
  pending: boolean;
  inviting: boolean;
  onInvite: () => void;
  onSessions: (sessions: number[]) => void;
  onStandDown: () => void;
}) {
  const sessions = parseSessions(entry.substitute.availableWeekdays);

  const toggle = (value: number) =>
    onSessions(sessions.includes(value) ? sessions.filter((n) => n !== value) : [...sessions, value]);

  return (
    <tr id={`sub-${entry.substitute.id}`} tabIndex={-1} className={`${PLAYER_ROW} scroll-mt-8 hover:bg-chalk target:ring-2 target:ring-deep`}>
      <Td kind="name" className="py-2">
        <Link to={`/director/players/${entry.player.id}`} className={PLAYER_NAME}>
          {entry.player.firstName} {entry.player.lastName}
        </Link>
        <span className="block whitespace-nowrap text-[13px] text-ink-2">
          {entry.player.email ?? 'No email'}
          {entry.player.phone ? ` · ${entry.player.phone}` : ''}
          {' · '}
          {ACCOUNT_LABEL[entry.account]}
        </span>
      </Td>
      <Td className="whitespace-nowrap text-[14px] text-ink">{paceLabel(entry.player.preferredPace)}</Td>
      <Td className="py-3">
        <div className="flex gap-1">
          {SESSIONS.map((session) => {
            const on = sessions.includes(session.value);
            return (
              <button
                key={session.value}
                type="button"
                disabled={pending}
                aria-pressed={on}
                onClick={() => toggle(session.value)}
                className={[
                  'rounded-card border px-2 py-1 text-[13px] transition-colors disabled:opacity-55',
                  on
                    ? 'border-deep bg-tint-deep font-medium'
                    : 'border-light text-ink-2 hover:border-stroke',
                ].join(' ')}
              >
                {session.label}
              </button>
            );
          })}
        </div>
        {sessions.length === 0 ? (
          /* Absent is not the same as "no sessions", and the copy has to keep them apart. */
          <span className="mt-1 block text-[12px] text-ink-3 sm:whitespace-nowrap">
            Nobody has asked them which sessions yet.
          </span>
        ) : null}
      </Td>
      {/*
        Derived from the absences, never stored. It is the sentence that earns somebody a seat
        of her own next season, so it must not be able to drift.
      */}
      <Td className="tabular text-[14px] text-ink-2">
        {entry.timesSubbed}
      </Td>
      <Td kind="actions">
        <div className="flex items-center justify-end gap-1 whitespace-nowrap">
          <InviteButton
            player={entry.player}
            account={entry.account}
            disabled={inviting}
            onInvite={onInvite}
          />
          <button type="button" className={`${QUIET_ACTION} hover:border-rust/35 hover:text-rust`} onClick={onStandDown}>
            Remove from sub list
          </button>
        </div>
      </Td>
    </tr>
  );
}

/** A substitute's details on a phone: the panel from the bottom (owner, 2026-10-16, design A). */
function SubSheet({
  entry,
  pending,
  inviting,
  onClose,
  onInvite,
  onSessions,
  onStandDown,
}: {
  entry: SubEntry;
  pending: boolean;
  inviting: boolean;
  onClose: () => void;
  onInvite: () => void;
  onSessions: (sessions: number[]) => void;
  onStandDown: () => void;
}) {
  const sessions = parseSessions(entry.substitute.availableWeekdays);
  const toggle = (value: number) =>
    onSessions(sessions.includes(value) ? sessions.filter((n) => n !== value) : [...sessions, value]);
  return (
    <Sheet
      open
      onClose={onClose}
      title={`${entry.player.firstName} ${entry.player.lastName}`}
      subtitle={<>{entry.player.email ?? 'No email'}{entry.player.phone ? ` · ${entry.player.phone}` : ''} · {ACCOUNT_LABEL[entry.account]}</>}
      footer={<>
        <button type="button" className={SHEET_DANGER} onClick={onStandDown}>Remove from sub list</button>
        <button type="button" className={SHEET_DONE} onClick={onClose}>Done</button>
      </>}
    >
      <Facts items={[
        { label: 'Pace of play', value: paceLabel(entry.player.preferredPace) },
        { label: 'Times subbed', value: entry.timesSubbed },
      ]} />
      <div>
        <p className="mb-2 text-[13px] text-ink-3">Available</p>
        <div className="flex gap-1.5">
          {SESSIONS.map((session) => {
            const on = sessions.includes(session.value);
            return (
              <button
                key={session.value}
                type="button"
                disabled={pending}
                aria-pressed={on}
                aria-label={session.label}
                onClick={() => toggle(session.value)}
                className={[
                  'flex h-10 w-10 items-center justify-center rounded-full border text-[14px] transition-colors disabled:cursor-wait',
                  on ? 'border-deep bg-deep font-semibold text-on-deep' : 'border-stroke bg-paper text-ink-2',
                ].join(' ')}
              >
                {session.label[0]}
              </button>
            );
          })}
        </div>
        {sessions.length === 0 ? <p className="mt-2 text-[12px] text-ink-3">Nobody has asked them which sessions yet.</p> : null}
      </div>
      <div className="flex">
        <InviteButton player={entry.player} account={entry.account} disabled={inviting} onInvite={onInvite} />
      </div>
    </Sheet>
  );
}

function AddSubstitute({ marketId, onDone, onCancel }: { marketId: string; onDone: () => void; onCancel: () => void }) {
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [preferredPace, setPreferredPace] = useState<PlayingPace>('unknown');
  const [phone, setPhone] = useState('');

  const add = useMutation({
    mutationFn: () =>
      apiPost('/api/substitutes', {
        marketId,
        newPlayer: {
          preferredPace,
          firstName: firstName.trim(),
          lastName: lastName.trim(),
          ...(email.trim() ? { email: email.trim() } : {}),
          ...(phone.trim() ? { phone: phone.trim() } : {}),
        },
      }),
    onSuccess: onDone,
  });

  return (
    <div className="flex flex-col gap-4 rounded-md border border-light bg-paper p-5">
      {add.error ? (
        <ErrorNote>
          {add.error instanceof ApiError ? add.error.message : 'Could not add this sub.'}
        </ErrorNote>
      ) : null}

      <div className="grid gap-4 sm:grid-cols-2">
        <Field label="First name" htmlFor="sub-first">
          <Input id="sub-first" value={firstName} onChange={(e) => setFirstName(e.target.value)} />
        </Field>
        <Field label="Last name" htmlFor="sub-last">
          <Input id="sub-last" value={lastName} onChange={(e) => setLastName(e.target.value)} />
        </Field>
      </div>

      {/*
        The address is what links her to the player she may already be. Somebody who played in this
        city last spring is the same person, and a second record would split her sub count in half.
      */}
      <Field
        label="Email"
        htmlFor="sub-email"
        hint="If they have played here before, this finds them rather than making a second record."
      >
        <Input id="sub-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
      </Field>

      <Field label="Player’s preferred playing pace" htmlFor="sub-pace" hint="Use what the player tells you. Existing players keep their saved preference.">
        <select id="sub-pace" className="min-h-11 w-full min-w-0 rounded-md border border-control-border bg-paper p-2" value={preferredPace} onChange={e => setPreferredPace(e.target.value as PlayingPace)}>{PACE_OPTIONS.map(p => <option value={p.value} key={p.value}>{p.label}</option>)}</select>
        <p className="text-sm text-ink-2">{PACE_OPTIONS.find(p => p.value === preferredPace)?.description}</p>
      </Field>
      <Field label="Phone" htmlFor="sub-phone" hint="Optional — but it is how you reach a sub.">
        <Input id="sub-phone" type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} />
      </Field>

      <div className="flex flex-wrap gap-3">
        <Button
          size="app"
          disabled={add.isPending || !firstName.trim() || !lastName.trim()}
          onClick={() => add.mutate()}
        >
          {add.isPending ? 'Adding…' : 'Add to the pool'}
        </Button>
        <Button variant="secondary" disabled={add.isPending} onClick={onCancel}>
          Cancel
        </Button>
      </div>
    </div>
  );
}
